// Shared jsPDF drawing helpers for the "Family Afya" style quote layout:
// navy header banner, section bars, calculation-details/total-due boxes,
// bulleted terms & conditions, and a bordered footer with a reference number.

export const THEME = {
  navy: [0, 0, 139],
  navyDark: [0, 0, 90],
  red: [178, 30, 46],
  lightGray: [240, 243, 248],
  borderGray: [200, 200, 200],
  grayText: [90, 90, 90],
};

export function generateReference(prefix) {
  const year = new Date().getFullYear();
  const serial = Math.floor(1000 + Math.random() * 9000);
  return `REF: ${prefix}/${year}/${serial}`;
}

export function drawHeaderBanner(doc, { title, logo, pageWidth, margin, timestampLabel }) {
  doc.setFillColor(...THEME.navy);
  doc.rect(0, 0, pageWidth, 8, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(255, 255, 255);
  doc.text(timestampLabel, margin, 5.5);
  doc.setTextColor(0, 0, 0);

  let y = 22;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...THEME.red);
  doc.text(title, margin, y);
  doc.setDrawColor(...THEME.red);
  doc.setLineWidth(1);
  doc.line(margin, y + 3, margin + 75, y + 3);
  doc.setTextColor(0, 0, 0);

  doc.addImage(logo, 'PNG', pageWidth - margin - 45, 10, 45, 20);

  return y + 14;
}

export function drawClientInfo(doc, clientDetails, y, margin) {
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Client Name:', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(clientDetails.name || '-', margin + 32, y);
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.text('Email:', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(clientDetails.email || '-', margin + 32, y);
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.text('Telephone:', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(clientDetails.phone || clientDetails.telephone || '-', margin + 32, y);
  return y + 10;
}

export function drawSectionBar(doc, text, y, margin, contentWidth, opts = {}) {
  const height = opts.height || 7;
  const fill = opts.fill || THEME.navy;
  doc.setFillColor(...fill);
  doc.rect(margin, y, contentWidth, height, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(opts.fontSize || 10);
  doc.text(text, margin + 3, y + height - 2);
  doc.setTextColor(0, 0, 0);
  return y + height + (opts.gap !== undefined ? opts.gap : 5);
}

export function ensureSpace(doc, y, needed, pageHeight, margin) {
  if (y + needed > pageHeight - margin) {
    doc.addPage();
    return margin;
  }
  return y;
}

export function drawBulletList(doc, lines, x, y, maxWidth, pageHeight, margin) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(40, 40, 40);
  lines.forEach(line => {
    const wrapped = doc.splitTextToSize(`• ${line}`, maxWidth);
    wrapped.forEach(textLine => {
      if (y + 5 > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(textLine, x, y);
      y += 5;
    });
  });
  doc.setTextColor(0, 0, 0);
  return y;
}

export function addPageNumber(doc, pageNumber, pageHeight, pageWidth, margin) {
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
  doc.setTextColor(0, 0, 0);
}

export function drawFooterBlock(doc, y, margin, pageWidth, getDate, reference, quoteLabel) {
  doc.setDrawColor(...THEME.borderGray);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageWidth - margin, y);
  y += 6;

  doc.setFillColor(...THEME.navy);
  doc.rect(margin, y - 3, 4, 4, 'F');
  doc.setFillColor(...THEME.red);
  doc.rect(margin + 5, y - 3, 4, 4, 'F');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...THEME.grayText);
  doc.text(`Quote Issued: ${getDate}`, margin + 12, y);
  doc.text(`Reference: ${reference}`, margin + 12, y + 5);
  doc.text('This quote is valid for 90 days from the date of issue', pageWidth / 2, y, { align: 'center' });
  doc.text(quoteLabel, pageWidth - margin, y, { align: 'right' });

  doc.setTextColor(0, 0, 0);
  return y + 10;
}

// Draws the "CALCULATION DETAILS" (left) and "TOTAL AMOUNT DUE" (right) boxes.
export function drawPremiumSummaryBoxes(doc, y, margin, contentWidth, data) {
  const {
    basicPremium, loadingPercentage = 0, loadingAmount = 0,
    discountPercentage = 0, discountAmount = 0, adjustedPremium,
    levies, stampDuty, stampDutyLabel, totalDue, totalMembers, totalFamilies,
    formatCurrency,
  } = data;

  const boxGap = 6;
  const boxWidth = (contentWidth - boxGap) / 2;
  const leftX = margin;
  const rightX = margin + boxWidth + boxGap;

  const rows = [['Basic Premium', formatCurrency(basicPremium)]];
  if (loadingPercentage > 0) {
    rows.push([`Loading Adjustment (+${loadingPercentage}%)`, formatCurrency(loadingAmount)]);
  }
  if (discountPercentage > 0) {
    rows.push([`Discount Adjustment (-${discountPercentage}%)`, `(${formatCurrency(discountAmount)})`]);
  }
  if (loadingPercentage > 0 || discountPercentage > 0) {
    rows.push(['Adjusted Premium', formatCurrency(adjustedPremium)]);
  }
  rows.push(['Levy (0.45%)', formatCurrency(levies)]);
  rows.push([stampDutyLabel || 'Stamp Duty', formatCurrency(stampDuty)]);

  const rowHeight = 8;
  const leftBoxHeight = 12 + rows.length * rowHeight + 4;
  const rightBoxHeight = leftBoxHeight;
  const boxHeight = Math.max(leftBoxHeight, rightBoxHeight, 60);

  // Left box
  doc.setDrawColor(...THEME.navy);
  doc.setLineWidth(0.6);
  doc.roundedRect(leftX, y, boxWidth, boxHeight, 2, 2, 'S');
  doc.setFillColor(...THEME.navy);
  doc.rect(leftX + 1, y + 1, boxWidth - 2, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.text('CALCULATION DETAILS', leftX + 4, y + 6.5);
  doc.setTextColor(0, 0, 0);

  let rowY = y + 15;
  rows.forEach(([label, value]) => {
    const isAdjusted = label === 'Adjusted Premium';
    doc.setFont('helvetica', isAdjusted ? 'bold' : 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...(isAdjusted ? THEME.red : [40, 40, 40]));
    doc.text(label, leftX + 4, rowY);
    doc.text(value, leftX + boxWidth - 4, rowY, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    rowY += rowHeight;
  });

  // Right box (Total Amount Due)
  doc.setFillColor(...THEME.navyDark);
  doc.roundedRect(rightX, y, boxWidth, boxHeight, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.text('TOTAL AMOUNT DUE', rightX + 4, y + 9);

  doc.setFontSize(20);
  doc.text(formatCurrency(totalDue), rightX + boxWidth / 2, y + boxHeight / 2 - 2, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text('(Inclusive of all taxes & levies)', rightX + boxWidth / 2, y + boxHeight / 2 + 6, { align: 'center' });

  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.2);
  doc.line(rightX + 4, y + boxHeight - 16, rightX + boxWidth - 4, y + boxHeight - 16);

  doc.setFontSize(8);
  doc.text('Total Members Covered:', rightX + 4, y + boxHeight - 10);
  doc.text(String(totalMembers), rightX + boxWidth - 4, y + boxHeight - 10, { align: 'right' });
  doc.text('Total Families:', rightX + 4, y + boxHeight - 4);
  doc.text(String(totalFamilies), rightX + boxWidth - 4, y + boxHeight - 4, { align: 'right' });

  doc.setTextColor(0, 0, 0);
  return y + boxHeight + 8;
}
